import React, { useEffect, useState, useRef } from 'react';
import { Bell, Calendar, MessageSquare, Check } from 'lucide-react';
import { adminApi } from '../lib/api';

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `il y a ${s}s`;
  if (s < 3600) return `il y a ${Math.floor(s / 60)}min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)}h`;
  return `il y a ${Math.floor(s / 86400)}j`;
};

const formatFCFA = (n) => new Intl.NumberFormat('fr-FR').format(n);

const NotificationsBell = () => {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(false);
  const containerRef = useRef(null);
  const lastCountRef = useRef(0);

  const load = async () => {
    try {
      const data = await adminApi.getNotifications();
      setItems(data.items || []);
      const newCount = data.count || 0;
      if (newCount > lastCountRef.current && lastCountRef.current > 0) {
        setPulse(true);
        setTimeout(() => setPulse(false), 1600);
      }
      lastCountRef.current = newCount;
      setCount(newCount);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    load();
    const int = setInterval(load, 30000); // poll every 30s
    return () => clearInterval(int);
  }, []);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const markAllRead = async () => {
    await adminApi.markNotificationsRead({ type: 'all' });
    setItems([]); setCount(0); lastCountRef.current = 0;
  };

  const markOneRead = async (id) => {
    await adminApi.markNotificationsRead({ ids: [id] });
    setItems(items.filter((n) => n.id !== id));
    setCount((c) => Math.max(0, c - 1));
    lastCountRef.current = Math.max(0, lastCountRef.current - 1);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setOpen(!open)}
        className={`relative p-2 rounded-full bg-white border transition-colors ${
          open ? 'border-[#F4A340] text-[#E89534]' : 'border-slate-200 hover:border-[#F4A340] hover:text-[#E89534]'
        }`}
        title="Notifications"
      >
        <Bell className={`w-4 h-4 ${pulse ? 'animate-bounce' : ''}`} />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 max-w-[90vw] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden z-30 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div>
              <div className="font-bold text-slate-900">Notifications</div>
              <div className="text-xs text-slate-500">{count} non lue{count > 1 ? 's' : ''}</div>
            </div>
            {items.length > 0 && (
              <button onClick={markAllRead} className="text-xs text-[#E89534] hover:text-[#F4A340] font-semibold px-2 py-1 rounded-lg hover:bg-orange-50 transition-colors">
                Tout marquer lu
              </button>
            )}
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                <Bell className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Tout est à jour !</p>
              </div>
            ) : (
              <ul className="divide-y divide-slate-50">
                {items.map((n) => {
                  const Icon = n.type === 'reservation' ? Calendar : MessageSquare;
                  const color = n.type === 'reservation' ? 'text-emerald-600 bg-emerald-100' : 'text-purple-600 bg-purple-100';
                  return (
                    <li key={n.id} className="p-3 hover:bg-orange-50/40 transition-colors flex items-start gap-3 group">
                      <div className={`w-9 h-9 rounded-xl ${color} flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-slate-900 truncate">{n.title}</div>
                        <div className="text-xs text-slate-600 truncate">{n.message}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] text-slate-400">{timeAgo(n.created_at)}</span>
                          {n.amount != null && (
                            <span className="text-[10px] font-bold text-[#E89534]">{formatFCFA(n.amount)} F</span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => markOneRead(n.id)}
                        title="Marquer comme lu"
                        className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-white text-slate-400 hover:text-emerald-600 transition-all"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="px-4 py-2 border-t border-slate-100 bg-slate-50/50 text-[10px] text-slate-400 text-center">
            Actualisation automatique toutes les 30 secondes
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsBell;
